import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Send,
  Loader2,
  Plus,
  Trash2,
  User,
  Mail,
  Phone,
  Home,
  MapPin,
  DollarSign,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  Search,
} from "lucide-react";
import {
  sendLeadToRealtor,
  LOAN_PURPOSE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  TIMELINE_OPTIONS,
} from "../services/LeadsService";
import { fetchPartners } from "../api/partners";

/**
 * SendToRealtorModal - Modal to send lead information to realtor(s)
 * Only shown to Ultra/Enterprise tier users
 */
const SendToRealtorModal = ({
  isOpen,
  onClose,
  lead,
}) => {
  const [realtors, setRealtors] = useState([{ email: "", name: "", isSelected: false }]);
  const ccEmail = "partners@nhamy.us"; // Fixed CC email for referral manager
  const [formData, setFormData] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    loan_purpose: "Purchase",
    property_type: "Single Family",
    property_location: "",
    price_range: "",
    timeline: "Within 3 months",
    additional_notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [availableRealtors, setAvailableRealtors] = useState([]);
  const [loadingRealtors, setLoadingRealtors] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const dropdownRefs = useRef({});

  // Pre-fill form with lead data when modal opens
  useEffect(() => {
    if (isOpen && lead) {
      // Get loan amount from various possible locations
      const loanAmount = lead.loan_details?.estimatedValue || lead.estimated_value || lead.loan_amount;

      // Get property location from address or state
      const propertyLocation = lead.loan_details?.propertyAddress ||
        (lead.state ? `${lead.city ? lead.city + ", " : ""}${lead.state}` : "");

      // Format price range
      let priceRange = "";
      if (loanAmount) {
        const amount = Number(String(loanAmount).replace(/[^0-9.-]/g, ""));
        if (!isNaN(amount) && amount > 0) {
          priceRange = `$${amount.toLocaleString()}`;
        }
      }

      setFormData({
        buyer_name: lead.contact_name || "",
        buyer_email: lead.contact_email || "",
        buyer_phone: lead.contact_phone || "",
        loan_purpose: lead.loan_details?.loanPurpose || "Purchase",
        property_type: lead.loan_details?.propertyType || lead.property_type || "Single Family",
        property_location: propertyLocation,
        price_range: priceRange,
        timeline: "Within 3 months",
        additional_notes: lead.loan_details?.additionalNotes || "",
      });
      setRealtors([{ email: "", name: "", isSelected: false }]);
      setResults(null);
      setError(null);
      setOpenDropdownIndex(null);
      setSearchTerms({});
    }
  }, [isOpen, lead]);

  // Fetch available realtor partners when modal opens
  useEffect(() => {
    const loadRealtors = async () => {
      if (!isOpen) return;
      setLoadingRealtors(true);
      try {
        const response = await fetchPartners();
        const partners = response?.data?.partners || response?.partners || [];
        // Filter only realtor type partners that are active
        const realtorPartners = partners.filter(
          (p) => p.partner_type === "realtor" && p.status === "active"
        );
        setAvailableRealtors(realtorPartners);
      } catch (err) {
        console.error("Failed to load realtor partners:", err);
        setAvailableRealtors([]);
      } finally {
        setLoadingRealtors(false);
      }
    };
    loadRealtors();
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownIndex !== null) {
        const dropdownRef = dropdownRefs.current[openDropdownIndex];
        if (dropdownRef && !dropdownRef.contains(event.target)) {
          setOpenDropdownIndex(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownIndex]);

  const handleRealtorChange = (index, field, value) => {
    const updated = [...realtors];
    updated[index][field] = value;
    // If user manually edits email or name, mark as not selected from list
    if (field === "email" || field === "name") {
      updated[index].isSelected = false;
    }
    setRealtors(updated);
  };

  const handleSelectRealtor = (index, partner) => {
    const updated = [...realtors];
    updated[index] = {
      email: partner.email,
      name: partner.name || partner.email.split("@")[0],
      isSelected: true,
    };
    setRealtors(updated);
    setOpenDropdownIndex(null);
    setSearchTerms((prev) => ({ ...prev, [index]: "" }));
  };

  const handleClearSelection = (index) => {
    const updated = [...realtors];
    updated[index] = { email: "", name: "", isSelected: false };
    setRealtors(updated);
  };

  const addRealtor = () => {
    if (realtors.length < 20) {
      setRealtors([...realtors, { email: "", name: "", isSelected: false }]);
    }
  };

  const removeRealtor = (index) => {
    if (realtors.length > 1) {
      setRealtors(realtors.filter((_, i) => i !== index));
      // Clean up search term for removed index
      setSearchTerms((prev) => {
        const newTerms = { ...prev };
        delete newTerms[index];
        return newTerms;
      });
    }
  };

  const getFilteredRealtors = (index) => {
    const searchTerm = (searchTerms[index] || "").toLowerCase();
    // Filter out already selected realtors
    const selectedEmails = realtors
      .filter((_, i) => i !== index)
      .map((r) => r.email.toLowerCase());

    return availableRealtors.filter((partner) => {
      const isAlreadySelected = selectedEmails.includes(partner.email.toLowerCase());
      if (isAlreadySelected) return false;

      if (!searchTerm) return true;

      const nameMatch = (partner.name || "").toLowerCase().includes(searchTerm);
      const emailMatch = partner.email.toLowerCase().includes(searchTerm);
      const companyMatch = (partner.company || "").toLowerCase().includes(searchTerm);
      return nameMatch || emailMatch || companyMatch;
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    // Validate realtors
    const validRealtors = realtors.filter((r) => r.email && r.name);
    if (validRealtors.length === 0) {
      setError("Please add at least one realtor with email and name");
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const realtor of validRealtors) {
      if (!emailRegex.test(realtor.email)) {
        setError(`Invalid email format: ${realtor.email}`);
        return false;
      }
    }

    // Validate required fields
    if (!formData.buyer_name.trim()) {
      setError("Buyer name is required");
      return false;
    }
    if (!formData.buyer_email.trim() || !emailRegex.test(formData.buyer_email)) {
      setError("Valid buyer email is required");
      return false;
    }
    if (!formData.buyer_phone.trim() || formData.buyer_phone.length < 10) {
      setError("Valid buyer phone is required (10+ characters)");
      return false;
    }
    if (!formData.property_location.trim()) {
      setError("Property location is required");
      return false;
    }
    if (!formData.price_range.trim()) {
      setError("Price range is required");
      return false;
    }

    // Validate notes length
    if (formData.additional_notes.length > 1000) {
      setError("Additional notes must be under 1000 characters");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const validRealtors = realtors.filter((r) => r.email && r.name);
      const response = await sendLeadToRealtor({
        realtors: validRealtors,
        cc_emails: [ccEmail], // CC referral manager on all realtor emails
        ...formData,
      });
      setResults(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRealtors([{ email: "", name: "", isSelected: false }]);
    setFormData({
      buyer_name: "",
      buyer_email: "",
      buyer_phone: "",
      loan_purpose: "Purchase",
      property_type: "Single Family",
      property_location: "",
      price_range: "",
      timeline: "Within 3 months",
      additional_notes: "",
    });
    setError(null);
    setResults(null);
    setOpenDropdownIndex(null);
    setSearchTerms({});
    onClose();
  };

  if (!isOpen) return null;

  // Show results after successful submission
  if (results) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          <div className="p-6">
            <div className="text-center mb-6">
              {results.total_failed === 0 ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Successfully Sent!
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400">
                    {results.message}
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Partially Sent
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400">
                    {results.total_sent} sent, {results.total_failed} failed
                  </p>
                </>
              )}
            </div>

            {/* Results list */}
            <div className="space-y-2 mb-6 max-h-48 overflow-y-auto">
              {results.results?.map((result, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    result.success
                      ? "bg-emerald-50 dark:bg-emerald-900/20"
                      : "bg-red-50 dark:bg-red-900/20"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {result.success ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white text-sm">
                        {result.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {result.email}
                      </p>
                    </div>
                  </div>
                  {!result.success && result.error && (
                    <span className="text-xs text-red-600 dark:text-red-400">
                      {result.error}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={handleClose}
              className="w-full px-4 py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-lg transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Send className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  Send to Realtor
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Share this lead with realtor partners
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Realtors Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Realtor(s) to Send To
                  </label>
                  <span className="text-xs text-slate-500">
                    {realtors.length}/20 max
                  </span>
                </div>
                <div className="space-y-3">
                  {realtors.map((realtor, idx) => (
                    <div
                      key={idx}
                      ref={(el) => (dropdownRefs.current[idx] = el)}
                      className="relative"
                    >
                      {/* Selected Realtor Display */}
                      {realtor.isSelected ? (
                        <div className="flex gap-3">
                          <div className="flex-1 flex items-center gap-3 px-3 py-2.5 border border-emerald-300 dark:border-emerald-700 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-medium text-sm">
                              {(realtor.name || realtor.email)[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white text-sm truncate">
                                {realtor.name}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {realtor.email}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleClearSelection(idx)}
                              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {realtors.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRealtor(idx)}
                              className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* Dropdown Trigger or Manual Input */}
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <div className="relative">
                                {availableRealtors.length > 0 || loadingRealtors ? (
                                  <>
                                    {loadingRealtors ? (
                                      <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500 animate-spin" />
                                    ) : (
                                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    )}
                                    <input
                                      type="text"
                                      value={
                                        openDropdownIndex === idx
                                          ? searchTerms[idx] || ""
                                          : realtor.email
                                      }
                                      onChange={(e) => {
                                        if (openDropdownIndex === idx) {
                                          setSearchTerms((prev) => ({
                                            ...prev,
                                            [idx]: e.target.value,
                                          }));
                                        } else {
                                          handleRealtorChange(idx, "email", e.target.value);
                                        }
                                      }}
                                      onFocus={() => setOpenDropdownIndex(idx)}
                                      placeholder={loadingRealtors ? "Loading realtors..." : "Search or type email..."}
                                      className="w-full pl-10 pr-10 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                    {loadingRealtors ? (
                                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-purple-500 font-medium">
                                        Loading...
                                      </span>
                                    ) : (
                                      <ChevronDown
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform ${
                                          openDropdownIndex === idx ? "rotate-180" : ""
                                        }`}
                                      />
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                      type="email"
                                      value={realtor.email}
                                      onChange={(e) =>
                                        handleRealtorChange(idx, "email", e.target.value)
                                      }
                                      placeholder="realtor@email.com"
                                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                  type="text"
                                  value={realtor.name}
                                  onChange={(e) =>
                                    handleRealtorChange(idx, "name", e.target.value)
                                  }
                                  placeholder="Agent Name"
                                  className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                            </div>
                            {realtors.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRealtor(idx)}
                                className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Dropdown Menu */}
                          {openDropdownIndex === idx && (availableRealtors.length > 0 || loadingRealtors) && (
                            <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {loadingRealtors ? (
                                <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-purple-500" />
                                  Loading realtor partners...
                                </div>
                              ) : getFilteredRealtors(idx).length > 0 ? (
                                <>
                                  <div className="px-3 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                                    Select from your realtor partners
                                  </div>
                                  {getFilteredRealtors(idx).map((partner) => (
                                    <button
                                      key={partner.email}
                                      type="button"
                                      onClick={() => handleSelectRealtor(idx, partner)}
                                      className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                                    >
                                      <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                                        {(partner.name || partner.email)[0].toUpperCase()}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-slate-900 dark:text-white text-sm truncate">
                                          {partner.name || partner.email.split("@")[0]}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                          {partner.email}
                                          {partner.company && ` · ${partner.company}`}
                                        </p>
                                      </div>
                                      <Home className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                    </button>
                                  ))}
                                </>
                              ) : (
                                <div className="px-3 py-4 text-center text-slate-500 dark:text-slate-400 text-sm">
                                  {searchTerms[idx]
                                    ? "No matching partners found"
                                    : "No more available partners"}
                                  <p className="text-xs mt-1">
                                    Type an email to add manually
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {realtors.length < 20 && (
                  <button
                    type="button"
                    onClick={addRealtor}
                    className="mt-3 flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add Another Realtor
                  </button>
                )}

                {/* CC Email Field - Fixed value */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    CC
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={ccEmail}
                      readOnly
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    Referral manager will be CC'd on all realtor notifications
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 dark:border-slate-700" />

              {/* Buyer Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Buyer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={formData.buyer_name}
                        onChange={(e) =>
                          handleInputChange("buyer_name", e.target.value)
                        }
                        placeholder="John Smith"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        value={formData.buyer_email}
                        onChange={(e) =>
                          handleInputChange("buyer_email", e.target.value)
                        }
                        placeholder="buyer@email.com"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Phone *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="tel"
                        value={formData.buyer_phone}
                        onChange={(e) =>
                          handleInputChange("buyer_phone", e.target.value)
                        }
                        placeholder="(555) 123-4567"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Loan Purpose *
                    </label>
                    <select
                      value={formData.loan_purpose}
                      onChange={(e) =>
                        handleInputChange("loan_purpose", e.target.value)
                      }
                      className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {LOAN_PURPOSE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Property Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Property Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Property Type *
                    </label>
                    <div className="relative">
                      <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <select
                        value={formData.property_type}
                        onChange={(e) =>
                          handleInputChange("property_type", e.target.value)
                        }
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                      >
                        {PROPERTY_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Location *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={formData.property_location}
                        onChange={(e) =>
                          handleInputChange("property_location", e.target.value)
                        }
                        placeholder="Houston, TX"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Price Range *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={formData.price_range}
                        onChange={(e) =>
                          handleInputChange("price_range", e.target.value)
                        }
                        placeholder="$300,000 - $400,000"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Timeline *
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <select
                        value={formData.timeline}
                        onChange={(e) =>
                          handleInputChange("timeline", e.target.value)
                        }
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                      >
                        {TIMELINE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Additional Notes (Optional)
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <textarea
                    value={formData.additional_notes}
                    onChange={(e) =>
                      handleInputChange("additional_notes", e.target.value)
                    }
                    placeholder="First-time homebuyer, pre-approved for $350,000..."
                    rows={3}
                    maxLength={1000}
                    className="w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1 text-right">
                  {formData.additional_notes.length}/1000
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send to Realtor{realtors.filter((r) => r.email && r.name).length > 1 ? "s" : ""}
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SendToRealtorModal;
